import React from "react";
import { Loader2 } from "lucide-react";

// Card primitive matching USN dashboard dark style
export function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`bg-[#0F172A] border border-white/5 rounded-xl ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 pt-5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#D4AF37]" />}
          <h3 className="text-[15px] font-semibold text-white tracking-tight truncate">{title}</h3>
        </div>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// Page header (title + action)
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
          Administration A.F.B.
        </div>
        <h1 className="font-display text-2xl md:text-3xl text-white mt-1.5">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// KPI card with colored icon block
export function KPICard({ icon: Icon, label, value, delta, color = "blue", testid }) {
  const palette = {
    blue: { bg: "rgba(59, 130, 246, 0.12)", fg: "#60A5FA" },
    green: { bg: "rgba(16, 185, 129, 0.12)", fg: "#34D399" },
    orange: { bg: "rgba(245, 158, 11, 0.12)", fg: "#FBBF24" },
    purple: { bg: "rgba(139, 92, 246, 0.12)", fg: "#A78BFA" },
    gold: { bg: "rgba(212, 175, 55, 0.12)", fg: "#D4AF37" },
    red: { bg: "rgba(239, 68, 68, 0.12)", fg: "#F87171" },
  }[color] || { bg: "rgba(59, 130, 246, 0.12)", fg: "#60A5FA" };

  return (
    <div
      className="bg-[#0F172A] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-10 h-10 rounded-lg grid place-items-center"
          style={{ background: palette.bg, color: palette.fg }}
        >
          <Icon size={18} />
        </div>
        {delta && (
          <span className="text-[11px] font-semibold text-emerald-400 inline-flex items-center gap-1">
            <span>↗</span> {delta}
          </span>
        )}
      </div>
      <div className="mt-5">
        <div className="font-display text-3xl text-white tracking-tight">{value}</div>
        <div className="text-xs text-slate-400 mt-1">{label}</div>
      </div>
    </div>
  );
}

// Empty state
export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="text-center py-16">
      {Icon && (
        <div className="w-12 h-12 rounded-xl bg-white/5 grid place-items-center mx-auto mb-3 text-slate-500">
          <Icon size={20} />
        </div>
      )}
      <div className="text-sm text-slate-300 font-medium">{title}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="py-12 flex justify-center">
      <Loader2 size={24} className="text-[#D4AF37] animate-spin" />
    </div>
  );
}

// Dark input
export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-2.5 rounded-lg bg-[#0B1220] border border-white/10 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#D4AF37]/60 ${props.className || ""}`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3.5 py-2.5 rounded-lg bg-[#0B1220] border border-white/10 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#D4AF37]/60 ${props.className || ""}`}
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-2.5 rounded-lg bg-[#0B1220] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-[#D4AF37]/60 ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}

// Primary button
export function PrimaryButton({ children, ...rest }) {
  return (
    <button
      {...rest}
      className={`px-4 py-2.5 rounded-lg bg-[#D4AF37] hover:bg-[#B5952F] text-[#0B1220] font-semibold text-sm inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, ...rest }) {
  return (
    <button
      {...rest}
      className={`px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 text-sm inline-flex items-center gap-2 transition-colors border border-white/10 ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

// Badge
export function Badge({ children, color = "blue" }) {
  const palette = {
    blue: "bg-[#0052CC]/20 text-[#7AB0FF] border-[#0052CC]/30",
    gold: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
    red: "bg-[#CE1126]/15 text-[#FCA5A5] border-[#CE1126]/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    slate: "bg-white/5 text-slate-300 border-white/10",
  }[color] || "bg-white/5 text-slate-300 border-white/10";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border ${palette}`}>
      {children}
    </span>
  );
}

// Table primitives
export function Table({ children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-white/5">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}
export function THead({ children }) {
  return (
    <thead className="bg-white/[0.03] text-slate-400 text-[11px] uppercase tracking-wider">
      {children}
    </thead>
  );
}
export function TR({ children, ...rest }) {
  return <tr className="border-t border-white/5 hover:bg-white/[0.02]" {...rest}>{children}</tr>;
}
export function TH({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 font-semibold ${className}`}>{children}</th>;
}
export function TD({ children, className = "" }) {
  return <td className={`px-4 py-3 text-slate-300 ${className}`}>{children}</td>;
}
